import { useMutation } from '@apollo/client';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  addNegativeToast,
  addNegativeToastWithErrorId,
  addPositiveToast,
} from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { MoveAssetContentsAndFoldersQuery } from 'api/queries/creativeLibrary';
import { CurrentSelection, SelectionTypes } from 'utils/contexts/selection';
import keyBy from 'lodash/keyBy';
import { ROOT_FOLDER_NAME } from '../constants';

enum KnownMoveFailures {
  FolderNotFoundFailure = 'FolderNotFoundFailure',
  DestinationNotFoundFailure = 'DestinationNotFoundFailure',
  FolderIsAncestorOfDestinationFailure = 'FolderIsAncestorOfDestinationFailure',
  FolderIsSameAsDestinationFailure = 'FolderIsSameAsDestinationFailure',
  FolderNameConflictFailure = 'FolderNameConflictFailure',
}

export type MoveAssetContentAndFoldersError = { __typename: string; id: number };

export type MovedContent = {
  id: number;
  name: string;
};

export type MoveAssetContentAndFoldersResponse = {
  moveAssetContentsAndFolders: {
    movedContent: MovedContent[];
    errors: MoveAssetContentAndFoldersError[];
  };
};

export type Variables = {
  folderIds: FolderId[];
  destinationId: FolderId;
  contentIds: number[];
};

const displayText = {
  genericError: 'Could not move items',
};

export type UseMoveAssetContentAndFoldersResult =
  MoveAssetContentAndFoldersResponse['moveAssetContentsAndFolders'] & {
    isLoading: boolean;
    refetch: () => void;
  };

export const useMoveAssetContentsAndFoldersQuery = () => {
  const dispatch = useDispatch();

  const [moveAssetContentsAndFolders] = useMutation<MoveAssetContentAndFoldersResponse, Variables>(
    MoveAssetContentsAndFoldersQuery,
  );

  return useCallback(
    async ({
      contentIds,
      folderIds,
      itemsToMove,
      destination,
    }: {
      contentIds: number[];
      folderIds: FolderId[];
      itemsToMove: CurrentSelection<number, string>[];
      destination: AncestorIdData & AncestorNameData;
    }) => {
      const itemsById = keyBy(itemsToMove, 'id');

      if (!contentIds?.length && !folderIds?.length) {
        return dispatch(addNegativeToast('No items selected to move'));
      }

      try {
        const { errors, data } = await moveAssetContentsAndFolders({
          variables: {
            contentIds,
            destinationId: destination.id,
            folderIds,
          },
        });

        if (!data && !!errors?.length) {
          dispatch(addNegativeToast('An unknown error occurred'));
        }

        const destinationFolderName =
          destination.name === '__root__' ? ROOT_FOLDER_NAME : destination.name;

        data?.moveAssetContentsAndFolders.errors.forEach((err) => {
          switch (err.__typename) {
            case KnownMoveFailures.FolderNotFoundFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> item can not be moved as{' '}
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> does not exist.
                  </ResponseWrapper>,
                ),
              );
              break;
            }
            case KnownMoveFailures.DestinationNotFoundFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> item can not be moved as{' '}
                    <NamedFolder name={destinationFolderName} /> does not exist.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            case KnownMoveFailures.FolderNameConflictFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    The item can not be moved into <NamedFolder name={destinationFolderName} /> as a
                    folder with that name already exists. This could also be because the folder is
                    already a child folder of <NamedFolder name={destinationFolderName} />.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            case KnownMoveFailures.FolderIsSameAsDestinationFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    The folder can not be moved into <NamedFolder name={destinationFolderName} /> as
                    a folder is the same.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            case KnownMoveFailures.FolderIsAncestorOfDestinationFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> folder can not be moved
                    into <NamedFolder name={itemsById[err.id]?.name || ''} /> folder or one of its
                    child folders.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            default: {
              dispatch(addNegativeToast('Item could not be moved.'));
            }
          }
        });

        data?.moveAssetContentsAndFolders.movedContent?.forEach((content) => {
          switch (itemsById[content.id].type) {
            case SelectionTypes.Content: {
              dispatch(
                addPositiveToast(
                  <ResponseWrapper>
                    {destination.name === '__root__' ? (
                      <>
                        <strong>Asset {content.name}</strong> was successfully moved to overview.
                      </>
                    ) : (
                      <>
                        <strong>Asset {content.name}</strong> was successfully moved into the{' '}
                        <NamedFolder name={destinationFolderName} /> folder.
                      </>
                    )}
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            case SelectionTypes.Folder: {
              dispatch(
                addPositiveToast(
                  <ResponseWrapper>
                    <NamedFolder name={content.name} /> was successfully moved into the{' '}
                    <NamedFolder name={destinationFolderName} /> folder.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            default: {
              console.error('Moved an unallowed content item: possible type mismatch'); // eslint-disable-line
            }
          }
        });

        return data?.moveAssetContentsAndFolders;
      } catch (error) {
        return dispatch(
          addNegativeToastWithErrorId(displayText.genericError, 'moveAssetContentsAndFolders'),
        );
      }
    },
    [moveAssetContentsAndFolders],
  );
};
